'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { HyperliquidWsClient, fetchCandleSnapshot, fetchAllMids } from './service';
import type {
  HLWsBook,
  HLWsTrade,
  HLCandle,
  HLActiveAssetCtx,
  OrderBookEntry,
  NormalizedTrade,
  NormalizedCandle,
  MarketStats,
} from './types';

// Map UI symbol (e.g. "HYPE/USDC") to Hyperliquid coin name (e.g. "HYPE")
function toCoin(symbol: string): string {
  return symbol.split('/')[0];
}

// Map UI timeframe to Hyperliquid interval
function toHLInterval(tf: string): string {
  const map: Record<string, string> = {
    '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1h', '4h': '4h', 'D': '1d', 'W': '1w',
  };
  return map[tf] ?? '1h';
}

function normalizeOrderBook(book: HLWsBook): { bids: OrderBookEntry[]; asks: OrderBookEntry[] } {
  const normalize = (levels: { px: string; sz: string; n: number }[]): OrderBookEntry[] => {
    let cumTotal = 0;
    return levels.map(lvl => {
      cumTotal += parseFloat(lvl.sz);
      return {
        price: parseFloat(lvl.px).toFixed(2),
        size: parseFloat(lvl.sz).toFixed(4),
        total: cumTotal.toFixed(4),
        depth: 0, // will be set after max total is known
      };
    });
  };

  const bids = normalize(book.levels[0] ?? []);
  const asks = normalize(book.levels[1] ?? []);

  // Compute depth relative to max cumulative total
  const maxBidTotal = bids.length ? parseFloat(bids[bids.length - 1].total) : 1;
  const maxAskTotal = asks.length ? parseFloat(asks[asks.length - 1].total) : 1;

  bids.forEach(b => { b.depth = Math.min(parseFloat(b.total) / maxBidTotal, 1); });
  asks.forEach(a => { a.depth = Math.min(parseFloat(a.total) / maxAskTotal, 1); });

  return { bids, asks: [...asks].reverse() }; // asks reversed so lowest ask is at bottom
}

function normalizeTrade(trade: HLWsTrade): NormalizedTrade {
  const d = new Date(trade.time);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const price = parseFloat(trade.px);
  const size = parseFloat(trade.sz);
  return {
    id: `${trade.tid}`,
    time: timeStr,
    side: trade.side === 'B' ? 'Buy' : 'Sell',
    price: price.toFixed(2),
    size: size.toFixed(4),
    value: (price * size).toFixed(2),
  };
}

function normalizeCandle(c: HLCandle): NormalizedCandle {
  return {
    time: Math.floor(c.t / 1000),
    open: parseFloat(String(c.o)),
    high: parseFloat(String(c.h)),
    low: parseFloat(String(c.l)),
    close: parseFloat(String(c.c)),
    volume: parseFloat(String(c.v)),
  };
}

export interface HyperliquidData {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  midPrice: string;
  spread: string;
  trades: NormalizedTrade[];
  candles: NormalizedCandle[];
  marketStats: MarketStats | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useHyperliquid(symbol: string, timeframe: string): HyperliquidData {
  const coin = toCoin(symbol);
  const interval = toHLInterval(timeframe);

  const [bids, setBids] = useState<OrderBookEntry[]>([]);
  const [asks, setAsks] = useState<OrderBookEntry[]>([]);
  const [midPrice, setMidPrice] = useState<string>('');
  const [spread, setSpread] = useState<string>('0.00');
  const [trades, setTrades] = useState<NormalizedTrade[]>([]);
  const [candles, setCandles] = useState<NormalizedCandle[]>([]);
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clientRef = useRef<HyperliquidWsClient | null>(null);
  const unsubsRef = useRef<Array<() => void>>([]);

  // Fetch initial candle history via REST
  const fetchInitialCandles = useCallback(async (c: string, iv: string) => {
    try {
      const endTime = Date.now();
      const startTime = endTime - 200 * getCandleDurationMs(iv);
      const data = await fetchCandleSnapshot(c, iv, startTime, endTime);
      setCandles(data.map(normalizeCandle));
    } catch (e) {
      // non-fatal, candles will update via WS
    }
  }, []);

  // Fetch initial mid prices via REST
  const fetchInitialMids = useCallback(async (c: string) => {
    try {
      const mids = await fetchAllMids();
      const mid = mids[c];
      if (mid) {
        setMidPrice(parseFloat(mid).toFixed(2));
      }
    } catch {
      // non-fatal
    }
  }, []);

  // Fetch daily candle to get 24h high/low
  const fetchDailyCandle = useCallback(async (c: string) => {
    try {
      const endTime = Date.now();
      const startTime = endTime - 86400000; // 24 hours
      const dailyCandles = await fetchCandleSnapshot(c, '1d', startTime, endTime);
      if (dailyCandles && dailyCandles.length > 0) {
        const daily = dailyCandles[dailyCandles.length - 1];
        const high = parseFloat(String(daily.h));
        const low = parseFloat(String(daily.l));
        const vol = parseFloat(String(daily.v));
        setMarketStats(prev => {
          if (!prev) {
            return {
              markPrice: '',
              openInterest: '',
              fundingRate: '',
              dayVolume: formatLargeNumber(vol),
              prevDayPx: '',
              change24h: '',
              positive: true,
              high24h: high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              low24h: low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            };
          }
          return {
            ...prev,
            high24h: high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            low24h: low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            dayVolume: formatLargeNumber(vol),
          };
        });
      }
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setBids([]);
    setAsks([]);
    setTrades([]);
    setCandles([]);
    setMarketStats(null);

    // Clean up previous subscriptions
    unsubsRef.current.forEach(u => u());
    unsubsRef.current = [];

    if (clientRef.current) {
      clientRef.current.destroy();
    }

    const client = new HyperliquidWsClient();
    clientRef.current = client;

    // Fetch initial data
    Promise.all([
      fetchInitialCandles(coin, interval),
      fetchInitialMids(coin),
      fetchDailyCandle(coin),
    ]).finally(() => {
      setIsLoading(false);
      setIsConnected(true);
    });

    client.connect();

    // Subscribe to l2Book
    const unsubBook = client.subscribe(
      { type: 'l2Book', coin },
      (data) => {
        const book = data as HLWsBook;
        if (!book?.levels) return;
        const { bids: newBids, asks: newAsks } = normalizeOrderBook(book);
        setBids(newBids);
        setAsks(newAsks);

        // Compute mid price and spread
        const bestBid = newBids[0] ? parseFloat(newBids[0].price) : 0;
        const bestAsk = newAsks[newAsks.length - 1] ? parseFloat(newAsks[newAsks.length - 1].price) : 0;
        if (bestBid && bestAsk) {
          const mid = ((bestBid + bestAsk) / 2).toFixed(2);
          const sp = (bestAsk - bestBid).toFixed(2);
          setMidPrice(mid);
          setSpread(sp);
        }
      }
    );

    // Subscribe to trades
    const unsubTrades = client.subscribe(
      { type: 'trades', coin },
      (data) => {
        const tradeArr = data as HLWsTrade[];
        if (!Array.isArray(tradeArr)) return;
        const normalized = tradeArr.map(normalizeTrade);
        setTrades(prev => {
          const combined = [...normalized, ...prev];
          return combined.slice(0, 50);
        });
      }
    );

    // Subscribe to candle updates
    const unsubCandle = client.subscribe(
      { type: 'candle', coin, interval },
      (data) => {
        const candle = data as HLCandle;
        if (!candle?.t) return;
        const normalized = normalizeCandle(candle);
        setCandles(prev => {
          if (!prev.length) return [normalized];
          const last = prev[prev.length - 1];
          if (last.time === normalized.time) {
            // Update last candle
            return [...prev.slice(0, -1), normalized];
          }
          return [...prev, normalized].slice(-500);
        });
      }
    );

    // Subscribe to activeAssetCtx for market stats
    const unsubCtx = client.subscribe(
      { type: 'activeAssetCtx', coin },
      (data) => {
        const ctx = data as HLActiveAssetCtx;
        if (!ctx?.ctx) return;
        const { markPx, prevDayPx, dayNtlVlm, funding, openInterest } = ctx.ctx;
        const change = prevDayPx > 0 ? ((markPx - prevDayPx) / prevDayPx) * 100 : 0;
        setMarketStats(prev => ({
          markPrice: parseFloat(String(markPx)).toFixed(2),
          openInterest: formatLargeNumber(openInterest * markPx),
          fundingRate: (parseFloat(String(funding)) * 100).toFixed(4) + '%',
          dayVolume: formatLargeNumber(dayNtlVlm),
          prevDayPx: parseFloat(String(prevDayPx)).toFixed(2),
          change24h: (change >= 0 ? '+' : '') + change.toFixed(2) + '%',
          positive: change >= 0,
          // Preserve high24h/low24h from daily candle fetch
          high24h: prev?.high24h,
          low24h: prev?.low24h,
        }));
      }
    );

    unsubsRef.current = [unsubBook, unsubTrades, unsubCandle, unsubCtx];

    return () => {
      unsubsRef.current.forEach(u => u());
      unsubsRef.current = [];
      client.destroy();
      setIsConnected(false);
    };
  }, [coin, interval, fetchInitialCandles, fetchInitialMids]);

  return { bids, asks, midPrice, spread, trades, candles, marketStats, isConnected, isLoading, error };
}

function getCandleDurationMs(interval: string): number {
  const map: Record<string, number> = {
    '1m': 60000, '3m': 180000, '5m': 300000, '15m': 900000,
    '30m': 1800000, '1h': 3600000, '2h': 7200000, '4h': 14400000,
    '8h': 28800000, '12h': 43200000, '1d': 86400000, '3d': 259200000,
    '1w': 604800000, '1M': 2592000000,
  };
  return map[interval] ?? 3600000;
}

function formatLargeNumber(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toFixed(2);
}
